import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';

export default function CSFooter() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    await base44.functions.invoke('newsletterSubscribe', { email, first_name: firstName, source: 'footer' });
    setSubscribed(true);
    setEmail('');
    setFirstName('');
    setLoading(false);
  };

  return (
    <footer>
      {/* Newsletter */}
      <div className="bg-cs-blue text-white py-10 px-4">
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-center gap-6">
          <div className="flex items-center gap-4 min-w-0">
            <Mail className="w-10 h-10 shrink-0 opacity-80" />
            <div>
              <p className="font-display font-bold text-lg">INSCRIVEZ-VOUS À NOTRE NEWSLETTER</p>
              <p className="text-sm text-blue-200">Recevez nos nouveautés, offres exclusives et promotions</p>
            </div>
          </div>
          <form onSubmit={handleSubscribe} className="flex w-full max-w-md flex-col gap-2 md:flex-1">
            {subscribed ? (
              <p className="text-green-300 font-medium">✓ Merci pour votre inscription !</p>
            ) : (
              <>
                <div className="flex w-full min-w-0 gap-0">
                  <input
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    type="text"
                    placeholder="Prénom"
                    className="w-24 sm:w-32 shrink-0 px-2 sm:px-3 py-2.5 rounded-l text-gray-800 text-sm focus:outline-none border-r border-gray-200"
                  />
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    type="email"
                    required
                    placeholder="Votre adresse e-mail"
                    className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 text-gray-800 text-sm focus:outline-none"
                  />
                  <button type="submit" disabled={loading} className="shrink-0 px-4 sm:px-5 py-2.5 bg-cs-dark text-white rounded-r font-bold text-sm hover:bg-black/70 transition-colors disabled:opacity-60">
                    {loading ? '...' : 'OK'}
                  </button>
                </div>
              </>
            )}
          </form>
          <div className="flex items-center gap-3 shrink-0">
            <p className="text-sm font-medium hidden md:block">SUIVEZ-NOUS</p>
            <a href="#" aria-label="Facebook" className="w-8 h-8 rounded bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"><Facebook className="w-4 h-4" /></a>
            <a href="#" aria-label="Instagram" className="w-8 h-8 rounded bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"><Instagram className="w-4 h-4" /></a>
            <a href="#" aria-label="YouTube" className="w-8 h-8 rounded bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"><Youtube className="w-4 h-4" /></a>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="bg-cs-dark text-gray-300 py-12 px-4">
        <div className="max-w-screen-xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <img
              src="https://base44.app/api/apps/69f6f4084329a31d2c3702bc/files/mp/public/69f6f4084329a31d2c3702bc/5d2c6e637_footer-logo.webp"
              alt="Circuit-Slot — Le circuit en Famille"
              width="122"
              height="68"
              loading="lazy"
              decoding="async"
              className="h-16 object-contain mb-3 rotate-180"
            />
            <p className="text-sm text-gray-300 leading-relaxed mb-4">
              Vous assister dans le choix<br/>
              et la pratique du circuit électrique voiture,<br/>
              pour que vous profitiez pleinement du bonheur d'être réuni en familles et entre amis.<br/>
              La course est une excuse pour rire ensemble.
            </p>
            {/* Payment logos */}
            <div className="flex gap-2 flex-wrap">
              {['CB', 'VISA', 'MC', 'PP'].map(p => (
                <div key={p} className="px-2 py-1 bg-white/10 rounded text-[10px] font-bold text-white">{p}</div>
              ))}
            </div>
          </div>

          <div>
            <p className="font-display font-semibold text-white mb-3 text-sm">INFORMATIONS</p>
            <div className="space-y-2 text-sm">
              <Link to="/a-propos" className="block hover:text-white transition-colors">À propos</Link>
              <Link to="/livraison" className="block hover:text-white transition-colors">Livraison</Link>
              <Link to="/blog" className="block hover:text-white transition-colors">Blog & Actualités</Link>
              <Link to="/contact" className="block hover:text-white transition-colors">Contact</Link>
            </div>
          </div>

          <div>
            <p className="font-display font-semibold text-white mb-3 text-sm">MON COMPTE</p>
            <div className="space-y-2 text-sm">
              <Link to="/profil" className="block hover:text-white transition-colors">Mon compte</Link>
              <Link to="/mes-commandes" className="block hover:text-white transition-colors">Mes commandes</Link>
              <Link to="/suivi-commande" className="block hover:text-white transition-colors">Suivre ma commande</Link>
              <Link to="/favoris" className="block hover:text-white transition-colors">Mes favoris</Link>
            </div>
          </div>

          <div>
            <p className="font-display font-semibold text-white mb-3 text-sm">SERVICES CLIENTS</p>
            <div className="space-y-2 text-sm">
              <Link to="/contact" className="block hover:text-white transition-colors">Contact</Link>
              <Link to="/cgv" className="block hover:text-white transition-colors">Retour &amp; Remboursement</Link>
              <Link to="/cgv" className="block hover:text-white transition-colors">Conditions générales de vente</Link>
              <Link to="/mentions-legales" className="block hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/politique-confidentialite" className="block hover:text-white transition-colors">Politique de confidentialité</Link>
            </div>
          </div>

          {user?.role === 'admin' && (
            <div>
              <p className="font-display font-semibold text-white mb-3 text-sm">GESTION</p>
              <div className="space-y-2 text-sm">
                <Link to="/admin/products" className="block hover:text-white transition-colors">Gestion des produits</Link>
                <Link to="/admin/orders" className="block hover:text-white transition-colors">Gestion des commandes</Link>
                <Link to="/admin/newsletter" className="block hover:text-white transition-colors">Newsletter</Link>
                <Link to="/admin/reviews" className="block hover:text-white transition-colors">Modération avis</Link>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-screen-xl mx-auto mt-10 pt-6 border-t border-white/10 text-center text-xs text-gray-400">
          © 2026 Circuit-Slot · Tous droits réservés
        </div>
      </div>
    </footer>
  );
}